import { format } from '@js/utilities/formatter.js'
import { Mask } from 'maska'
import { loanTermAlign } from '@js/utilities/loanDetails.js'
import { fieldTransformations } from '@js/utilities/fields'

export default {
    setCurrentRouteStep: ({ state, getters }, currentRoute) => {
        const allSteps = getters.journeyInfo.getCurrentJourneyPath()

        for (const step of allSteps) {
            if (step.route.name == currentRoute.name) {
                state.currentRouteStep = step
                return
            }
        }

        state.currentRouteStep = null
    },
    setFieldValue: ({ getters }, payload) => {
        const field = getters.journeyInfo.getField(payload.name)

        if (field) {
            let value = fieldTransformations(field, payload.value)
            field.value = value
        }

        return !!field
    },
    setDisplayValue: ({ getters }, payload) => {
        const field = getters.journeyInfo.getField(payload.name)

        if (field) {
            field.displayValue = payload.value
        }
    },
    removeCurrentStepFromJourney: ({ getters }, payload) => {
        const step = getters.firstUncompletedStep
        step.excludeFromJourney = true
    },
    markStepComplete: ({ getters }, payload) => {
        let step = payload.step
        if (typeof step == 'string') {
            step = getters.stepByName(step)
        }

        step.completed = payload?.completed !== undefined ? payload?.completed : true
    },
    invalidateFutureSteps: ({ getters }, route) => {
        const journeyPath = getters.journeyInfo.getCurrentJourneyPath()
        let upToStep = null

        const futureSteps = getters.journeyInfo.getFuturePotentialSteps()
        for (const step of futureSteps) {
            step.completed = false
        }

        for (const step of journeyPath) {
            if (step.route.path == route) {
                upToStep = step
                break
            }
        }

        if (upToStep) {
            // invalidate backwards so that journey doesn't refresh to beginning of for while setting values
            for (let i = journeyPath.length - 1; i >= 0; i--) {
                const step = journeyPath[i]

                if (upToStep == step) {
                    break
                }

                step.completed = false
            }
        }
    },
    fillInSteps: async ({ state, getters, dispatch }, inFields) => {
        if (inFields === undefined || !Object.keys(inFields).length) {
            return
        }

        let currentStep = null
        let run = true

        while (run) {
            // refresh the journey with the fields that have been entered previously to update expression logic
            // (why we need a while loop)
            const journeyPath = getters.journeyInfo.getCurrentJourneyPath()

            if (currentStep === null) {
                currentStep = journeyPath[0]
            } else {
                let nextStep = null
                let useNextStep = false
                for (const step of journeyPath) {
                    if (useNextStep) {
                        nextStep = step
                        break
                    }

                    if (currentStep == step) {
                        useNextStep = true
                    }
                }

                if (currentStep == nextStep) {
                    // we haven't moved - quit the while
                    run = false // NOSONAR
                    break
                }

                currentStep = nextStep
            }

            if (!currentStep) {
                run = false // NOSONAR
                break
            }

            const fields = currentStep?.fields || {}
            for (const fieldName in fields) {
                const stepField = fields[fieldName]

                if (stepField.ignorePrepopulation) {
                    continue
                }

                let getKey = null
                if (inFields.hasOwnProperty(stepField.mappedKey)) {
                    getKey = stepField.mappedKey
                } else if (inFields.hasOwnProperty(stepField.legacyMappedKey)) {
                    getKey = stepField.legacyMappedKey
                }

                let fieldValue = null
                if (getKey) {
                    fieldValue = stepField?.formatterOnQuery
                        ? format(inFields[getKey], stepField.formatterOnQuery)
                        : inFields[getKey]
                } else if ('defaultQueryValue' in stepField) {
                    fieldValue = stepField.defaultQueryValue
                }

                if (fieldValue !== null) {
                    // mask any fields
                    if (stepField?.mask) {
                        const maskValue = new Mask({ mask: stepField.mask })
                        fieldValue = maskValue.masked(fieldValue)
                    }

                    if (fieldName === 'loanTerm') {
                        const loanAmountField = getters.field('loanAmount')
                        fieldValue = loanTermAlign(
                            inFields[loanAmountField.mappedKey] ??
                                inFields[loanAmountField.legacyMappedKey] ??
                                loanAmountField?.value,
                            fieldValue,
                        )
                    }

                    if (fieldName === 'mobileNumber' && process.env.TERRITORY === 'usa') {
                        dispatch('updateValidationRules', {
                            field: 'mobileNumber',
                            rules: {
                                loqateMobilePhone: false,
                                phoneUS: true,
                            },
                        })
                    }

                    if (getKey === 'monthly_income') {
                        fieldValue = fieldValue * 12
                    }

                    stepField.value = fieldValue

                    // first occurance of full summary field in query string, will result in summary showing
                    if (
                        !state.has.fullSummary &&
                        getters.isPathTrajectoryDefined &&
                        !stepField.hideOnSummary &&
                        (state.smallSummary != 'with_values' || stepField?.section != getters.firstSection)
                    ) {
                        state.has.fullSummary = true
                    }
                }
            }
        }

        state.has.smallSummary =
            state.smallSummaryIntent == 'always' ||
            (state.smallSummaryIntent == 'with_values' && getters.smallSummaryFieldsFilled)
    },
    updateValidationRules: ({ getters }, payload) => {
        const field = getters.journeyInfo.getField(payload.field)
        for (const ruleName in payload.rules) {
            if (Object.hasOwnProperty.call(payload.rules, ruleName)) {
                const ruleValue = payload.rules[ruleName]
                field.validationRules[ruleName] = ruleValue
            }
        }
    },
    updateStepSetting: ({ getters }, payload) => {
        let step = payload.step
        if (typeof step == 'string') {
            step = getters.stepByName(step)
        }

        step[payload.setting] = payload.settingValue
    },
    setFieldError: ({ getters }, payload) => {
        const field = getters.journeyInfo.getField(payload.name)

        if (!field) {
            console.error('Cannot find field to set error: ' + payload.name)
            return
        }

        if (!field.hasOwnProperty('serverError')) {
            field['serverError'] = null
        }

        field.serverError = payload.message
        field.step.completed = false
    },
    clearFieldErrors: ({ getters }) => {
        for (const step of getters.journeyInfo.getCurrentJourneyPath()) {
            if (!step.fields) {
                continue
            }

            for (const name in step.fields) {
                const field = step.fields[name]

                if (field.hasOwnProperty('serverError')) {
                    field['serverError'] = null
                }
            }
        }
    },
    clearSteps: ({ getters }, payload) => {
        const stepNames = Array.isArray(payload) ? payload : payload.stepNames
        const changeToHideContinue = Array.isArray(payload) ? true : payload.changeToHideContinue

        for (const stepName of stepNames) {
            const step = getters.stepByName(stepName)

            if (!step) {
                continue
            }

            const fields = step?.fields || {}
            for (const fieldName in fields) {
                const field = fields[fieldName]
                field.value = null
            }

            if (changeToHideContinue) {
                step.hideContinue = true
            }

            step.completed = false
        }
    },
}
